<template>
  <div
    class="element-foreground-entry"
    :class="{ 'is-active': isActive }"
  >
    <span class="element-foreground-entry__text">
      <template v-if="element.title">
        {{ element.title }}
      </template>
      <span v-else class="placeholder-text">
        {{ $t("labels.unnamed") }}
      </span>
    </span>

    <!-- value -->
    <span class="whitespace-nowrap">
      <animated-number
        :value="value"
        class="element-foreground-entry__value"
      />
      <span>{{ element.unit }}</span>
    </span>

    <!-- show impulses in simulation mode -->
    <template v-if="inSimulationView">
      <scene-element-impulses
        class="element-foreground-entry__impulses"
        :item-type="element.type"
        :item-id="element.id"
        :impulse="impulse"
        :effects="openEffects"
        :open-cost="cost"
        :value="value"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useRoute } from "vue-router";

import { useElements } from "../element/elements";
import { useSimulationViewStore } from "@editor/views/simulation/simulationView.store";

import AnimatedNumber from "@core/wrappers/AnimatedNumber.vue";
import SceneElementImpulses from "@editor/items/sceneElement/SceneElementImpulses.vue";
import type { ImpulseItem } from "../impulse/ImpulseItem";
import type { BaseEffectItem } from "../effect/EffectItem";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { Uid } from "@/helpers/utility/Id";

const props = withDefaults(
  defineProps<{
    elementUid: Uid<"resource" | "target">;
    impulse?: ImpulseItem;
    openEffects: BaseEffectItem[];
    cost?: { value: number; actionId: number }[];
    value?: number;
    selection?: CanvasSelection;
  }>(),
  {
    cost: undefined
  }
);

const route = useRoute();
const elements = useElements();
const simulationView = useSimulationViewStore();

const element = computed(() => elements.byUid[props.elementUid]);

const inSimulationView = computed(() => {
  return ["simulation", "inspector"].includes(
    route.meta.areaName as string
  );
});

const isActive = computed(() => props.selection?.active?.has(element.value?.uid) ?? false);

const value = computed((): number => {
  if (props.value != null) return props.value;
  if (!inSimulationView.value || simulationView.selectedRound == null) {
    return element.value?.value ?? 0;
  }
  return Number(simulationView.selectedRound.elementValues[element.value.uid]?.value ?? 0);
});
</script>

<style lang="scss">
.element-foreground-entry {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 0;

  &.is-active {
    color: var(--color-action);
    font-weight: 600;
  }

  &__text {
    width: 100%;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__value {
    padding: 0 0.25em;
  }

  &__impulse {
    position: absolute;
    bottom: 100%;
    right: 0;
    padding: 0.5em;
    background-color: var(--color-background-deep);
    border: 1px solid var(--color-border);

    .is-active & {
      background-color: var(--color-action);
      border-color: var(--color-action);
      color: var(--color-action-contrast);
    }
  }

  &__impulses {
    bottom: 100%;
    top: auto;
  }
}
</style>
